/**
 * Exercise Library Page (Admin) - Epic 3
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import {
  ArrowLeft,
  Plus,
  Search,
  Dumbbell,
  Video,
  Trash2,
  Edit2,
  Loader2,
  Upload,
  X,
  Play,
} from "lucide-react";
import {
  useExercises,
  useMuscleGroups,
  createExercise,
  updateExercise,
  deleteExercise,
  seedMuscleGroups,
  uploadMedia,
} from "../hooks/useWorkout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Toaster, toast } from "sonner";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function ExerciseLibraryPage() {
  const navigate = useNavigate();
  const { exercises, isLoading, mutate } = useExercises();
  const { muscleGroups, mutate: mutateMuscleGroups } = useMuscleGroups();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMuscle, setFilterMuscle] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [deleteExerciseId, setDeleteExerciseId] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      video_url: "",
      cloudinary_public_id: "",
      muscle_group_ids: [],
    },
  });
  
  const watchedMuscleGroups = watch("muscle_group_ids") || [];
  
  // Seed muscle groups if empty
  const handleSeedMuscleGroups = async () => {
    try {
      await seedMuscleGroups();
      mutateMuscleGroups();
      toast.success("Groupes musculaires créés");
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  // Open dialog for new exercise
  const handleOpenNew = () => {
    setEditingExercise(null);
    reset({
      name: "",
      description: "",
      video_url: "",
      cloudinary_public_id: "",
      muscle_group_ids: [],
    });
    setDialogOpen(true);
  };
  
  // Open dialog for edit
  const handleOpenEdit = (exercise) => {
    setEditingExercise(exercise);
    reset({
      name: exercise.name,
      description: exercise.description || "",
      video_url: exercise.video_url || "",
      cloudinary_public_id: exercise.cloudinary_public_id || "",
      muscle_group_ids: exercise.muscle_groups?.map((mg) => mg.id) || [],
    });
    setDialogOpen(true);
  };
  
  // Handle video upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const result = await uploadMedia(file);
      setValue("video_url", result.secure_url);
      setValue("cloudinary_public_id", result.public_id);
      toast.success("Vidéo uploadée");
    } catch (err) {
      toast.error(err.message || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };
  
  // Submit form
  const onSubmit = async (data) => {
    try {
      if (editingExercise) {
        await updateExercise(editingExercise.id, data);
        toast.success("Exercice mis à jour");
      } else {
        await createExercise(data);
        toast.success("Exercice créé");
      }
      setDialogOpen(false);
      mutate();
    } catch (err) {
      toast.error(err.message || "Erreur");
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    if (!deleteExerciseId) return;
    
    try {
      await deleteExercise(deleteExerciseId);
      toast.success("Exercice supprimé");
      mutate();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleteExerciseId(null);
    }
  };
  
  // Toggle muscle group selection
  const toggleMuscleGroup = (id) => {
    const current = watchedMuscleGroups;
    if (current.includes(id)) {
      setValue("muscle_group_ids", current.filter((x) => x !== id));
    } else {
      setValue("muscle_group_ids", [...current, id]);
    }
  };
  
  // Filter exercises
  const filteredExercises = exercises.filter((ex) => {
    const matchesSearch = ex.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMuscle =
      filterMuscle === "all" ||
      ex.muscle_groups?.some((mg) => mg.id === filterMuscle);
    return matchesSearch && matchesMuscle;
  });
  
  return (
    <div className="min-h-screen bg-zinc-950">
      <Toaster position="top-right" richColors />
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/admin")}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <h1 className="font-heading text-xl font-bold text-white">
                BIBLIOTHÈQUE D'EXERCICES
              </h1>
            </div>
            
            <Button
              onClick={handleOpenNew}
              data-testid="add-exercise-button"
              className="h-10 px-4 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Rechercher un exercice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-exercises"
              className="pl-11 bg-zinc-900/50 border-zinc-800 h-11"
            />
          </div>
          
          <Select value={filterMuscle} onValueChange={setFilterMuscle}>
            <SelectTrigger className="w-full sm:w-[200px] bg-zinc-900/50 border-zinc-800 h-11">
              <SelectValue placeholder="Groupe musculaire" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">Tous les groupes</SelectItem>
              {muscleGroups.map((mg) => (
                <SelectItem key={mg.id} value={mg.id}>
                  {mg.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {muscleGroups.length === 0 && (
            <Button
              variant="outline"
              onClick={handleSeedMuscleGroups}
              className="border-zinc-700"
            >
              Initialiser groupes
            </Button>
          )}
        </div>
        
        {/* Exercises Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
          </div>
        ) : filteredExercises.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-16 text-center">
              <Dumbbell className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400 mb-2">Aucun exercice trouvé</p>
              <p className="text-zinc-600 text-sm">
                {exercises.length === 0
                  ? "Créez votre premier exercice"
                  : "Essayez de modifier vos filtres"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {filteredExercises.map((exercise) => (
              <motion.div key={exercise.id} variants={itemVariants}>
                <Card
                  className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors group"
                  data-testid={`exercise-card-${exercise.id}`}
                >
                  <CardContent className="p-0">
                    {/* Video Preview */}
                    <div className="relative aspect-video bg-zinc-950 rounded-t-lg overflow-hidden">
                      {exercise.video_url ? (
                        <>
                          <video
                            src={exercise.video_url}
                            className="w-full h-full object-cover"
                            muted
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play className="w-12 h-12 text-white" />
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-12 h-12 text-zinc-700" />
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-semibold text-white mb-2">
                        {exercise.name}
                      </h3>
                      
                      {exercise.description && (
                        <p className="text-zinc-500 text-sm mb-3 line-clamp-2">
                          {exercise.description}
                        </p>
                      )}
                      
                      {/* Muscle Groups */}
                      {exercise.muscle_groups?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {exercise.muscle_groups.map((mg) => (
                            <Badge
                              key={mg.id}
                              variant="outline"
                              className="text-xs border-zinc-700 text-zinc-400"
                            >
                              {mg.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenEdit(exercise)}
                          data-testid={`edit-exercise-${exercise.id}`}
                          className="flex-1 border-zinc-700 hover:bg-zinc-800"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteExerciseId(exercise.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
      
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">
              {editingExercise ? "Modifier l'exercice" : "Nouvel exercice"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {editingExercise
                ? "Modifiez les informations de l'exercice"
                : "Créez un nouvel exercice pour votre bibliothèque"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Nom *</Label>
              <Input
                {...register("name", { required: "Nom requis" })}
                placeholder="Développé Couché"
                data-testid="exercise-name-input"
                className="bg-zinc-950 border-zinc-800 h-11"
              />
              {errors.name && (
                <p className="text-red-400 text-sm">{errors.name.message}</p>
              )}
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                {...register("description")}
                placeholder="Instructions d'exécution..."
                data-testid="exercise-description-input"
                className="bg-zinc-950 border-zinc-800 min-h-[100px]"
              />
            </div>
            
            {/* Video Upload */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Vidéo d'exécution</Label>
              <div className="flex gap-2">
                <Input
                  {...register("video_url")}
                  placeholder="URL de la vidéo"
                  className="bg-zinc-950 border-zinc-800 h-11 flex-1"
                  readOnly
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="video/*,image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploading}
                    className="h-11 border-zinc-700"
                    asChild
                  >
                    <span>
                      {uploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </span>
                  </Button>
                </label>
              </div>
              <p className="text-zinc-600 text-xs">
                Upload vidéo/image vers Cloudinary (nécessite configuration)
              </p>
            </div>
            
            {/* Muscle Groups */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Groupes musculaires</Label>
              <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto p-2 bg-zinc-950 rounded-lg border border-zinc-800">
                {muscleGroups.map((mg) => (
                  <div
                    key={mg.id}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`mg-${mg.id}`}
                      checked={watchedMuscleGroups.includes(mg.id)}
                      onCheckedChange={() => toggleMuscleGroup(mg.id)}
                    />
                    <label
                      htmlFor={`mg-${mg.id}`}
                      className="text-sm text-zinc-300 cursor-pointer"
                    >
                      {mg.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="submit-exercise"
              className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full mt-4"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : editingExercise ? (
                "Mettre à jour"
              ) : (
                "Créer l'exercice"
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteExerciseId}
        onOpenChange={() => setDeleteExerciseId(null)}
      >
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Supprimer l'exercice ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Cette action est irréversible. L'exercice sera supprimé de votre bibliothèque.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
